import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { initHashtagFeed } from "actions/sourceMedia";
import { updateLibraryUrl } from "libs/media-library/url-sync";
import {
  getActiveHashtags,
  getExpiredHashtags,
  getInvalidIgAccount,
  getSelectedHashtagId,
  getSelectedIgAccount,
} from "selectors/hashtagFeedSelectors";
import { getRoutinesLoading } from "selectors/commonSelectors";
import { getAccountsForPlatform } from "selectors/skedCoreSelectors";
import {
  SKED_CORE_GET_ACCOUNTS_ROUTINE,
  VALIDATE_IG_ACCOUNT_ROUTINE,
  HASHTAG_FEED_GET_RECENT_HASHTAGS_ROUTINE,
} from "constants/ActionTypes";
import { AccountType } from "shared/types/accounts";
import type { ReduxRootState } from "types/redux-store";

import ActiveHashtags from "./ActiveHashtags";
import ExpiredHashtags from "./ExpiredHashtags";
import SelectFeedAccount from "./../SelectFeedAccount";

interface HashtagData {
  hashtag_id: string;
  name: string;
  search_date?: string;
  expired?: boolean;
}

interface UseHashtagsReturn {
  activeHashtags: HashtagData[];
  expiredHashtags: HashtagData[];
  invalidIgAccount: boolean;
  selectedIgAccount: AccountType | null;
  loadingAccounts: boolean;
  loadingHashtags: boolean;
  selectedHashtag: HashtagData | undefined;
}

export function useHashtags(): UseHashtagsReturn {
  const activeHashtags = useSelector(getActiveHashtags);
  const expiredHashtags = useSelector(getExpiredHashtags);
  const invalidIgAccount = useSelector(getInvalidIgAccount);
  const selectedIgAccount = useSelector(getSelectedIgAccount);
  const selectedHashtagId = useSelector(getSelectedHashtagId);
  const selectedHashtag = activeHashtags.find(
    (hashtag) => hashtag.hashtag_id === selectedHashtagId
  );
  const loadingAccounts = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [SKED_CORE_GET_ACCOUNTS_ROUTINE])
  );
  const loadingHashtags = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [
      VALIDATE_IG_ACCOUNT_ROUTINE,
      HASHTAG_FEED_GET_RECENT_HASHTAGS_ROUTINE,
    ])
  );

  return {
    activeHashtags,
    expiredHashtags,
    invalidIgAccount,
    selectedIgAccount,
    loadingAccounts,
    loadingHashtags,
    selectedHashtag,
  };
}

const HashtagSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasInitialized = useRef(false);
  const {
    activeHashtags,
    expiredHashtags,
    invalidIgAccount,
    selectedIgAccount,
    loadingAccounts,
    loadingHashtags,
  } = useHashtags();

  const accounts = useSelector((state: ReduxRootState) =>
    getAccountsForPlatform(state, "IG")
  );

  // Auto-load hashtag list when component mounts with a pre-selected account
  useEffect(() => {
    if (hasInitialized.current) return;

    if (selectedIgAccount && !loadingAccounts && accounts.length > 0) {
      const accountExists = accounts.some(
        (acc) => acc._id === selectedIgAccount._id
      );
      if (accountExists) {
        hasInitialized.current = true;
        dispatch(initHashtagFeed(selectedIgAccount._id));
      }
    }
  }, [selectedIgAccount, loadingAccounts, accounts, dispatch]);

  const handleSearch = (item: AccountType): void => {
    dispatch(initHashtagFeed(item._id));

    // Update URL with selected account
    const newParams = updateLibraryUrl(searchParams, {
      tab: "hashtags",
      account: item._id,
    });
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  if (loadingAccounts) {
    return null;
  }

  return (
    <div data-testid="hashtagSidebarContent" className="pb-4">
      <div className="px-3 py-1">
        <SelectFeedAccount onClick={handleSearch} accounts={accounts} />
      </div>
      {!loadingHashtags && selectedIgAccount && !invalidIgAccount && (
        <div>
          <ActiveHashtags
            account={selectedIgAccount}
            hashtags={activeHashtags}
            selectedIgAccount={selectedIgAccount}
          />
          <ExpiredHashtags
            account={selectedIgAccount}
            expiredHashtags={expiredHashtags}
            activeHashtags={activeHashtags}
          />
        </div>
      )}
    </div>
  );
};

export default HashtagSidebar;
